// Company picker — the post-sign-in "which company?" gate and the in-app switch
// control (AUTH-001, rbac-spec §5.3). A promise-based in-DOM modal on the shared
// confirm-dialog surface (never window.prompt — native dialogs block the
// automation extension and can't be asserted in jsdom). Resolves with the chosen
// companyId, or null when a cancelable picker is dismissed.
//
// Two modes:
//   - gate (cancelable: false): shown when a multi-company session lands unbound.
//     It cannot be dismissed — the session has no active company until one is
//     picked, so Escape / backdrop are inert and there is no Cancel.
//   - switch (cancelable: true): opened from the account screen to change the
//     active company; Escape / backdrop / Cancel resolve null (no change).

import type { CompanyMembership } from '../api/client';
import { currentUser, listCompanies, switchCompany } from '../services/auth';
import { t } from '../i18n';
import { roleLabel } from '../utils/roleLabel';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Per-picker id prefix for the options' aria-labelledby targets.
let pickerSeq = 0;

export interface CompanyPickerOptions {
  title?: string;
  message?: string;
  cancelable?: boolean;
  activeCompanyId?: string;
}

export function promptCompanyPicker(
  companies: CompanyMembership[],
  opts: CompanyPickerOptions = {},
): Promise<string | null> {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const cancelable = opts.cancelable ?? false;

  const overlay = document.createElement('div');
  overlay.className = 'confirm company-picker';

  const dialog = document.createElement('div');
  dialog.className = 'confirm__dialog';
  dialog.setAttribute('role', cancelable ? 'dialog' : 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');
  const titleId = 'company-picker-title';
  const descId = 'company-picker-desc';
  dialog.setAttribute('aria-labelledby', titleId);
  dialog.setAttribute('aria-describedby', descId);

  const title = document.createElement('h2');
  title.className = 'confirm__title';
  title.id = titleId;
  title.textContent = opts.title ?? t('company.title');

  const message = document.createElement('p');
  message.className = 'confirm__message';
  message.id = descId;
  message.textContent = opts.message ?? t('company.message');

  // A real list of real buttons (issue 736): the option used to be a button
  // with role="listitem", which overrode its button role — screen readers
  // announced a list item and role queries found no button. The explicit
  // role="list" keeps the list semantics Safari drops under list-style: none.
  const list = document.createElement('ul');
  list.className = 'company-picker__list';
  list.setAttribute('role', 'list');
  const idBase = `company-picker-${++pickerSeq}`;

  dialog.append(title, message, list);

  let cancel: HTMLButtonElement | null = null;
  if (cancelable) {
    const actions = document.createElement('div');
    actions.className = 'confirm__actions';
    cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn btn--secondary confirm__btn--cancel';
    cancel.textContent = t('common.cancel');
    actions.append(cancel);
    dialog.append(actions);
  }

  overlay.append(dialog);

  return new Promise<string | null>((resolve) => {
    let settled = false;

    function close(result: string | null): void {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
      resolve(result);
    }

    function onKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        if (!cancelable) return; // the gate can't be escaped
        e.preventDefault();
        close(null);
        return;
      }
      if (e.key === 'Tab') {
        const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const activeEl = document.activeElement as HTMLElement | null;
        if (e.shiftKey && activeEl === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    companies.forEach((c, i) => {
      const item = document.createElement('li');
      item.className = 'company-picker__item';
      const opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'company-picker__option';
      const isActive = c.companyId === opts.activeCompanyId;
      if (isActive) opt.dataset.active = 'true';

      const name = document.createElement('span');
      name.className = 'company-picker__name';
      name.id = `${idBase}-${i}-name`;
      name.textContent = c.name;

      const role = document.createElement('span');
      role.className = 'company-picker__role';
      role.id = `${idBase}-${i}-role`;
      role.textContent = isActive
        ? t('company.current', { role: roleLabel(c.roleId) })
        : roleLabel(c.roleId);

      // Name the button from both lines explicitly: name-from-content joins
      // two spans with no separator in some engines ("EXAAdmin").
      opt.setAttribute('aria-labelledby', `${name.id} ${role.id}`);
      opt.append(name, role);
      opt.addEventListener('click', () => close(c.companyId));
      item.append(opt);
      list.append(item);
    });

    if (cancel) cancel.addEventListener('click', () => close(null));
    overlay.addEventListener('click', (e) => {
      if (cancelable && e.target === overlay) close(null);
    });
    document.addEventListener('keydown', onKeydown, true);

    document.body.append(overlay);
    const initial =
      list.querySelector<HTMLElement>('[data-active="true"]') ?? list.querySelector<HTMLElement>('button');
    initial?.focus();
  });
}

// ensureCompanyChosen presents the sign-in gate picker when the session is
// authenticated but still unbound with more than one company (AUTH-001.3). Called
// by the boot flows (verify page, app boot) after authInit, which has already
// auto-bound a lone-company session and re-bound an already-bound one — so this
// is a no-op unless a genuine multi-company choice is pending. Resolves once the
// session is bound (or there is nothing to pick). Never throws: a failed bind
// leaves the session unbound and the app fails closed on tenancy-gated calls.
export async function ensureCompanyChosen(): Promise<void> {
  const me = currentUser();
  if (!me || me.companyId) return; // unauthenticated, or authInit already bound it
  let companies: CompanyMembership[];
  try {
    companies = await listCompanies();
  } catch {
    return;
  }
  if (companies.length <= 1) return; // 0 → nothing to pick; 1 → authInit auto-bound
  const chosen = await promptCompanyPicker(companies, {
    message: t('company.multiPrompt'),
  });
  if (!chosen) return;
  try {
    await switchCompany(chosen);
  } catch {
    // Stays unbound; the app fails closed until a successful bind.
  }
}
